import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';

const VideoDownloader = () => {
    const [url, setUrl] = useState('');
    const [videoData, setVideoData] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [downloadProgress, setDownloadProgress] = useState(0);
    const [isDownloading, setIsDownloading] = useState(false);
    const [error, setError] = useState('');
    const [sessionId, setSessionId] = useState(null);

    // Validate YouTube URL
    const isValidYouTubeUrl = (inputUrl) => {
        const youtubeRegex = /^(https?:\/\/)?(www\.)?(youtube\.com\/(watch\?v=|embed\/|shorts\/)|youtu\.be\/)[\w-]+/;
        return youtubeRegex.test(inputUrl);
    };

    // Automatically fetch video info when a valid URL is entered
    useEffect(() => {
        if (isValidYouTubeUrl(url)) {
            fetchVideoInfo(url);
        } else {
            setVideoData(null);
            setError('');
        }
    }, [url]);

    const fetchVideoInfo = async (videoUrl) => {
    setIsLoading(true);
    setError('');
    setVideoData(null);
    setDownloadProgress(0);
    setIsDownloading(false);
    setSessionId(null);

    // Exponential backoff logic for retrying failed API calls
    const MAX_RETRIES = 3;
    let retries = 0;

    while (retries < MAX_RETRIES) {
        try {
            const response = await fetch('https://zeedownloader-api.onrender.com/fetch_info', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ url: videoUrl }),
            });

            const data = await response.json();

            if (response.ok) {
                // Correctly setting video data from the API response
                setVideoData({
                    title: data.title,
                    thumbnail: data.thumbnail,
                });
                setIsLoading(false);
                return; // Exit the function on success
            } else {
                setError(data.error || 'Failed to fetch video information.');
                setIsLoading(false);
                return; // Exit on non-retryable error
            }
        } catch (err) {
            retries++;
            if (retries < MAX_RETRIES) {
                // Wait before retrying, doubling the delay each time
                const delay = Math.pow(2, retries) * 1000;
                await new Promise(res => setTimeout(res, delay));
            } else {
                // If max retries reached, set a final error message
                setError('Network error. Please check your connection.');
                setIsLoading(false);
            }
        }
    }
};


    const handleDownload = async (downloadType) => {
        if (!videoData) {
            setError('Video data not available. Please enter a valid URL.');
            return;
        }

        setIsDownloading(true);
        setDownloadProgress(0);
        setError('');

        try {
            const startResponse = await fetch('https://zeedownloader-api.onrender.com/download', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ url, type: downloadType }),
            });

            if (!startResponse.ok) {
                const errorData = await startResponse.json();
                throw new Error(errorData.error || 'Failed to start download process.');
            }

            const { session_id } = await startResponse.json();
            setSessionId(session_id);

            const progressInterval = setInterval(async () => {
                const progressResponse = await fetch(`https://zeedownloader-api.onrender.com/progress/${session_id}`);
                const progressData = await progressResponse.json();

                setDownloadProgress(progressData.progress);

                if (progressData.status === 'completed') {
                    clearInterval(progressInterval);
                    setIsDownloading(false);
                    setDownloadProgress(100);
                    window.location.href = `https://zeedownloader-api.onrender.com/download_file/${session_id}`;
                    setSessionId(null);
                } else if (progressData.status === 'error') {
                    clearInterval(progressInterval);
                    setIsDownloading(false);
                    setError(progressData.error || 'An error occurred during download.');
                    setDownloadProgress(0);
                    setSessionId(null);
                }
            }, 1000);

        } catch (err) {
            setError(err.message || 'Download failed. Please try again.');
            setIsDownloading(false);
            setDownloadProgress(0);
        }
    };

    // Handle paste from clipboard
    const handlePaste = async () => {
        try {
            const text = await navigator.clipboard?.readText();
            if (text) {
                setUrl(text);
                setError('');
            }
        } catch (err) {
            setError('Unable to access clipboard');
        }
    };

    // Clear form
    const handleClear = () => {
        setUrl('');
        setVideoData(null);
        setError('');
        setDownloadProgress(0);
        setIsDownloading(false);
        setSessionId(null);
    };

    return (
        <div className="w-full max-w-2xl mx-auto space-y-6">
            {/* URL Input Section */}
            <div className="glass-card p-6 space-y-4">
                <div className="text-center space-y-2">
                    <h1 className="text-fluid-3xl font-bold text-foreground">
                        YouTube Video Downloader
                    </h1>
                    <p className="text-fluid-base text-muted-foreground">
                        Download your favorite YouTube videos in high quality
                    </p>
                </div>

                <div className="flex gap-2">
                    <div className="flex-1">
                        <Input
                            type="url"
                            placeholder="Paste YouTube URL here..."
                            value={url}
                            onChange={(e) => setUrl(e.target.value)}
                            error={error}
                            className="text-base"
                            disabled={isDownloading}
                        />
                    </div>
                    <Button
                        variant="outline"
                        size="default"
                        iconName="Clipboard"
                        onClick={handlePaste}
                        className="shrink-0"
                        disabled={isLoading || isDownloading}
                    >
                        Paste
                    </Button>
                    <Button
                        variant="outline"
                        size="default"
                        iconName="X"
                        onClick={handleClear}
                        className="shrink-0"
                    >
                        Clear
                    </Button>
                </div>
            </div>

            {/* Loading/Error State */}
            {isLoading && (
                <div className="glass-panel p-4 text-center">
                    <p className="text-muted-foreground">Fetching video information...</p>
                </div>
            )}
            {error && (
                <div className="glass-panel p-4 text-center text-red-500">
                    <p>{error}</p>
                </div>
            )}

            {/* Video Preview and Download Options */}
            {videoData && !isDownloading && (
                <div className="glass-card p-6 space-y-4 animate-in slide-in-from-bottom-4 duration-500">
                    <div className="flex items-start gap-4">
                        <div className="relative shrink-0">
                            <Image
                                src={videoData?.thumbnail}
                                alt={videoData?.title}
                                className="w-32 h-20 object-cover rounded-lg"
                            />
                            <div className="absolute bottom-1 right-1 bg-black/80 text-white text-xs px-1.5 py-0.5 rounded">
                                {/* Backend does not provide this info, so it will be empty */}
                            </div>
                        </div>

                        <div className="flex-1 min-w-0">
                            <h3 className="text-fluid-lg font-semibold text-foreground line-clamp-2 mb-2">
                                {videoData?.title}
                            </h3>
                            <div className="space-y-1 text-sm text-muted-foreground">
                                <p className="flex items-center gap-2">
                                    <Icon name="User" size={14} />
                                    {/* Backend does not provide this info, so it will be empty */}
                                </p>
                                <p className="flex items-center gap-2">
                                    <Icon name="Eye" size={14} />
                                    {/* Backend does not provide this info, so it will be empty */}
                                </p>
                            </div>
                        </div>
                    </div>

                    <div className="pt-4 border-t border-border/20 flex gap-2">
                        <Button
                            variant="default"
                            size="default"
                            iconName="Video"
                            onClick={() => handleDownload('video')}
                            className="flex-1 glass-hover"
                        >
                            Download Video
                        </Button>
                        <Button
                            variant="default"
                            size="default"
                            iconName="Music"
                            onClick={() => handleDownload('audio')}
                            className="flex-1 glass-hover"
                        >
                            Download Audio
                        </Button>
                    </div>
                </div>
            )}

            {/* Download Progress */}
            {isDownloading && (
                <div className="glass-card p-6 space-y-3">
                    <div className="flex items-center justify-between text-sm">
                        <span className="text-muted-foreground">
                            Downloading...
                        </span>
                        <span className="font-medium text-foreground">
                            {Math.round(downloadProgress)}%
                        </span>
                    </div>

                    <div className="w-full bg-muted rounded-full h-2 overflow-hidden">
                        <div
                            className="h-full bg-gradient-to-r from-accent to-primary transition-all duration-300 ease-out"
                            style={{ width: `${downloadProgress}%` }}
                        />
                    </div>
                </div>
            )}

            {downloadProgress === 100 && (
                <div className="glass-card p-6 flex items-center gap-2 text-sm text-success">
                    <Icon name="CheckCircle" size={16} />
                    <span>Video downloaded successfully!</span>
                </div>
            )}

            {/* Features Section */}
            <div className="glass-card p-6">
                <h2 className="text-fluid-xl font-semibold text-foreground mb-4">
                    Why Choose Zee Downloader?
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {[
                        {
                            icon: 'Zap',
                            title: 'Lightning Fast',
                            description: 'Download videos at maximum speed with optimized servers'
                        },
                        {
                            icon: 'Shield',
                            title: 'Secure & Safe',
                            description: 'Your privacy is protected with SSL encryption'
                        },
                        {
                            icon: 'Smartphone',
                            title: 'Mobile Friendly',
                            description: 'Works perfectly on all devices and screen sizes'
                        },
                        {
                            icon: 'Star',
                            title: 'High Quality',
                            description: 'Support for 4K, 1080p, 720p, and more formats'
                        }
                    ].map((feature, index) => (
                        <div key={index} className="flex items-start gap-3 p-3 rounded-lg glass-light">
                            <div className="glass-card p-2 shrink-0">
                                <Icon name={feature.icon} size={20} className="text-accent" />
                            </div>
                            <div>
                                <h3 className="font-medium text-foreground mb-1">{feature.title}</h3>
                                <p className="text-sm text-muted-foreground">{feature.description}</p>
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
};

export default VideoDownloader;